import type { ChangeEvent } from "react";
import { useState, useMemo } from "react";
import "./StrapLengthSlider.scss";

const strapLengthLabelCopy = "Strap Length (Inches)";
const lengthPrefixCopy = "Length: ";
const inchesCopy = "inches";

export const StrapLengthSlider = ({
  min = 0,
  max = 100,
  step = 1,
  value: initialValue = 50,
  onChange,
  hideMarkers = false,
  markerInterval,
}: {
  min?: number;
  max?: number;
  step?: number;
  value: number;
  onChange: (value: number) => void;
  label?: string;
  hideMarkers?: boolean;
  markerInterval?: number;
}) => {
  const [value, setValue] = useState(initialValue);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(e.target.value);
    setValue(newValue);
    if (onChange) onChange(newValue);
  };

  // Generate marker positions - show fewer markers but always include min and max
  const markers = useMemo(() => {
    if (hideMarkers) return [];

    const range = max - min;

    // Calculate a reasonable interval to show ~5-8 markers
    // If markerInterval is provided, use it; otherwise calculate based on range
    let interval: number;
    if (markerInterval) {
      interval = markerInterval;
    } else {
      // Calculate interval to show approximately 6-7 markers
      const targetMarkerCount = 6;
      interval = Math.ceil(range / targetMarkerCount);
      // Round to nearest 5 for cleaner numbers
      interval = Math.ceil(interval / 5) * 5;
      // Ensure interval is at least the step size
      interval = Math.max(interval, step);
    }

    const markerValues: number[] = [min]; // Always include min

    // Add intermediate markers
    for (let i = min + interval; i < max; i += interval) {
      markerValues.push(i);
    }

    // Always include max if it's not already included
    if (markerValues[markerValues.length - 1] !== max) {
      markerValues.push(max);
    }

    return markerValues;
  }, [min, max, step, hideMarkers, markerInterval]);

  return (
    <div style={{ marginBottom: "60px" }}>
      <span className={`managed-select__copy`}>{strapLengthLabelCopy}</span>
      <div style={{ marginTop: 4, marginBottom: 6, fontWeight: 500 }}>
        {lengthPrefixCopy}
        {value} {inchesCopy}
      </div>
      <div className={"strap-length-slider-container"}>
        <input
          type={"range"}
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={handleChange}
          className={"strap-length-range-slider"}
        />
        {!hideMarkers && (
          <div className={"strap-length-slider-markers"}>
            {markers.map((markerValue) => (
              <div
                key={markerValue}
                className={"strap-length-slider-marker"}
                style={{
                  left: `${((markerValue - min) / (max - min)) * 100}%`,
                }}
              >
                <span className={"strap-length-slider-marker-tick"} />
                <span className={"strap-length-slider-marker-label"}>
                  {markerValue}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StrapLengthSlider;
